import axios from 'axios';
import { usePage } from '@inertiajs/vue3';
import { lang } from '@/lang/composable';
import { DraftConflictError, DraftRequestError } from '@src/Common/Builder/host';
import DocTable from '@src/Common/Builder/Document/DocTable.vue';
import type { BlockNode } from '@src/Common/Builder/types';
import type { BlockTemplate, BuilderHost, DocumentCapability, DraftService, DraftState } from '@src/Common/Builder/host';
import type { SharedBlockInfo, TypeInfo } from '@src/Common/Builder/useBuilderRegistry';

/**
 * Content host — binds the builder engine to the Content module backend:
 * the v1 type/block registry endpoints, the render/preview endpoints built
 * around RenderLevelTree markers, the page_drafts working copy and the
 * content:: lang namespace. Wire-identical to the pre-host builder.
 */

type ContentEntity = 'page' | 'block' | 'news';

/** GET fallbacks wrap the listing ({types: […]}), page props are bare arrays. */
async function fetchList<T>(routeName: string, key: string): Promise<T[]> {
    const { data } = await axios.get(route(routeName));

    return Array.isArray(data) ? data : ((data?.[key] as T[] | undefined) ?? []);
}

const translate = (key: string, params?: Record<string, unknown>): string =>
    lang(`content::content.builder_v2.${key}`, params as never);

/** Absent when the BUILDER_DOCUMENT_MODE kill-switch is off. */
function contentDocumentCapability(): DocumentCapability | undefined {
    const features = usePage().props.contentFeatures as { documentMode?: boolean } | undefined;

    if (!features?.documentMode) {
        return undefined;
    }

    return {
        default: 'canvas',
        transforms: { heading: 'headline' },
        components: { ContentDocTable: DocTable },
        media: {
            async upload(file) {
                const form = new FormData();
                form.append('file', file);
                const { data } = await axios.post(route('media.admin.api.create'), form);

                return data.object as unknown;
            },
            imageBlock: (media) => ({
                type: 'image',
                useBlock: false,
                value: { image: media },
                settings: {},
            }),
        },
    };
}

const block = (type: string, extra: Partial<BlockNode> = {}): BlockNode => ({
    type,
    useBlock: false,
    value: {},
    settings: {},
    ...extra,
});

/** Empty-canvas starters; the engine drops templates whose slugs are not in
 *  the tenant's type registry, so exotic tenants degrade gracefully. */
function contentTemplates(): BlockTemplate[] {
    return [
        {
            key: 'hero',
            label: translate('template_hero'),
            blocks: [
                block('section', {
                    children: [block('headline', { slot: 0 }), block('text', { slot: 0 })],
                }),
            ],
        },
        {
            key: 'text-image',
            label: translate('template_text_image'),
            blocks: [
                block('section', {
                    children: [
                        block('columns', {
                            slot: 0,
                            value: { cols: 2 },
                            children: [block('text', { slot: 0 }), block('image', { slot: 1 })],
                        }),
                    ],
                }),
            ],
        },
        {
            key: 'gallery',
            label: translate('template_gallery'),
            blocks: [block('section', { children: [block('gallery', { slot: 0 })] })],
        },
    ];
}

function contentDraftService(entity: ContentEntity, entityId: number): DraftService {
    const draftUrl = route('content.builder.draft.show', { entity, id: entityId });

    const conflictAware = async <T>(request: Promise<T>): Promise<T> => {
        try {
            return await request;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 409) {
                    throw new DraftConflictError();
                }

                throw new DraftRequestError(error.response?.status ?? 0);
            }

            throw error;
        }
    };

    return {
        async show(options = {}) {
            const { data } = await axios.get(draftUrl, { params: options.takeover ? { takeover: 1 } : {} });

            return data as DraftState;
        },
        async save(content) {
            const { data } = await conflictAware(axios.put(draftUrl, { content }));

            return data as { saved_at: string };
        },
        saveBeacon(content) {
            const token = decodeURIComponent(document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)?.[1] ?? '');

            void fetch(draftUrl, {
                method: 'PUT',
                keepalive: true,
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': token },
                body: JSON.stringify({ content }),
            });
        },
        async heartbeat() {
            await axios.post(route('content.builder.draft.heartbeat', { entity, id: entityId }));
        },
        async publish(force) {
            await conflictAware(axios.post(route('content.builder.draft.publish', { entity, id: entityId }), { force }));
        },
        async discard() {
            await axios.delete(route('content.builder.draft.discard', { entity, id: entityId }));
        },
    };
}

/**
 * Full-builder host: draft-backed preview, page-prop registry shortcut,
 * working copy on the given entity.
 *
 * @param entity   Draft-backed entity kind.
 * @param entityId Entity primary key.
 * @return BuilderHost Content-bound host implementation.
 */
export function createContentBuilderHost(entity: ContentEntity, entityId: number): BuilderHost {
    const props = usePage().props;

    return {
        catalog: {
            loadTypes: async () =>
                (props.types as TypeInfo[] | undefined) ?? (await fetchList<TypeInfo>('content.builder.types', 'types')),
            loadSharedBlocks: async () =>
                (props.blocks as SharedBlockInfo[] | undefined) ??
                (await fetchList<SharedBlockInfo>('content.builder.blocks', 'blocks')),
        },
        render: {
            async renderLevel(level: BlockNode) {
                const { data } = await axios.post(route('content.builder.render.level'), { level });

                return data as { html: string };
            },
            async renderTree(levels: BlockNode[]) {
                const { data } = await axios.post(route('content.builder.render.tree'), { levels });

                return data as { html: string };
            },
        },
        preview: {
            previewUrl: (reload) => route('content.builder.preview', { entity, id: entityId, reload }),
            breakpoints: async () => {
                const theme = usePage().props.builderTheme as
                    | { breakpoints?: { tablet?: number; mobile?: number } }
                    | undefined;

                return theme?.breakpoints?.tablet && theme.breakpoints.mobile
                    ? { tablet: theme.breakpoints.tablet, mobile: theme.breakpoints.mobile }
                    : null;
            },
        },
        draft: contentDraftService(entity, entityId),
        document: contentDocumentCapability(),
        templates: contentTemplates(),
        t: translate,
    };
}

/**
 * Canvas-lite host: harness preview, GET-only containerless types, no shared
 * blocks (Fields\Builder::render pins expandSharedBlocks=false), no draft —
 * the embedding form owns the data. Renders with the 'field' context so the
 * preview matches production Fields output.
 *
 * @return BuilderHost Content-bound Canvas-lite host implementation.
 */
export function createContentLiteHost(): BuilderHost {
    return {
        catalog: {
            loadTypes: () => fetchList<TypeInfo>('content.builder.types', 'types'),
        },
        render: {
            async renderLevel(level: BlockNode) {
                const { data } = await axios.post(route('content.builder.render.level'), { level, context: 'field' });

                return data as { html: string };
            },
            async renderTree(levels: BlockNode[]) {
                const { data } = await axios.post(route('content.builder.render.tree'), {
                    levels,
                    context: 'field',
                });

                return data as { html: string };
            },
        },
        preview: {
            previewUrl: (reload) => route('content.builder.preview.lite', { reload }),
            breakpoints: async () => {
                try {
                    const { data } = await axios.get(route('content.builder.theme'));
                    const breakpoints = data?.theme?.breakpoints;

                    return breakpoints?.tablet && breakpoints?.mobile
                        ? { tablet: Number(breakpoints.tablet), mobile: Number(breakpoints.mobile) }
                        : null;
                } catch {
                    return null;
                }
            },
        },
        t: translate,
    };
}
